/** RFC 0345 3h. An invite is a one-time key the owner grants, sealed with a
 * secret that only travels in the link's fragment. The auth-worker stores the
 * sealed bytes and releases them to the invited address; it cannot open them.
 * The key's material carries the signed public events it needs, so accepting
 * needs no relay round trip: the one-time key admits the guest's person and the
 * owner's device grants them again from the owner, then revokes the key (3j).
 */
import {
  type AnyDocumentId,
  stringifyAutomergeUrl,
} from '@automerge/automerge-repo/slim'
import {
  Access,
  CiphertextStore,
  ContactCard,
  DocumentId,
  docIdFromAutomergeUrl,
  GroupId,
  Identifier,
  Keyhive,
  MemberedId,
  Signer,
} from '@automerge/automerge-repo-keyhive'
import { readApp } from '../model/apps/reads'
import {
  boundCommentsUnder,
  grantComments,
  guestCommentsGrant,
  provisionGuestComments,
} from './comments'
import { revokeGroupMemberAndDirectInvitees } from './removal'
import type { createSharingRepo } from './repo'
import { canonicalBytes, signBytes, verifyBytes } from './signatures'

type Sharing = Awaited<ReturnType<typeof createSharingRepo>>
/** 'comment' is a document guest's: Read on the page, write on its comments. */
export type InviteLevel = 'admin' | 'edit' | 'comment' | 'read'
export type InviteTargetKind = 'project' | 'session' | 'document'

export interface InviteTarget {
  kind: InviteTargetKind
  /** The project, session or document the guest opens. */
  documentId: string
  /** What the guest joins: a project, session, or standalone share group.
   * A document id is accepted only as input to createInvite, before its
   * share-specific group is minted. */
  membered: { kind: 'group' | 'document'; id: string }
  title: string
  /** Display metadata only, never a source of access. */
  document?: { path: string; mime: string }
  /** A document target's embeds, at the page's level, and what draws them,
   * at Read (3d). Each is granted, accepted, settled and revoked with it. */
  includes?: InviteInclude[]
  /** The comments binding checked at invite creation, never a grant source. */
  commentsId?: string
}

export interface InviteInclude {
  documentId: string
  /** The grant's document, hex, checked against `documentId`. */
  id: string
  level: 'edit' | 'comment' | 'read'
  why: 'embed' | 'app' | 'view'
  title: string
  /** Display metadata for the guest's catalog, never a source of access. */
  document?: { path: string; mime: string; dataType?: string }
  app?: Record<string, unknown>
}

const RANK: Record<InviteLevel, number> = {
  read: 0,
  comment: 1,
  edit: 2,
  admin: 3,
}

/** The grant id a document id names. A grant and the entry filed for it must
 * be the same document, so this is checked wherever either is read. */
function grantIdOf(documentId: string) {
  try {
    return hex(
      docIdFromAutomergeUrl(`automerge:${documentId}` as never).toBytes(),
    )
  } catch {
    throw new Error('Invalid invite target')
  }
}

/** What a target's includes may hold. Only a document carries them; an
 * embed never outranks its page and what draws one is Read only. Each
 * include's grant is the document it names, never another. */
function includesOf(target: InviteTarget, level: InviteLevel) {
  const includes = target.includes ?? []
  if (includes.length && target.kind !== 'document')
    throw new Error('Only a document carries its embeds')
  const seen = new Set([target.documentId])
  for (const include of includes) {
    if (!/^[0-9a-f]{64}$/.test(include.id))
      throw new Error('Invalid invite target')
    if (grantIdOf(include.documentId) !== include.id)
      throw new Error(`${include.title} does not match its grant`)
    if (seen.has(include.documentId))
      throw new Error(`${include.title} is named twice`)
    seen.add(include.documentId)
    if (include.why !== 'embed' && include.level !== 'read')
      throw new Error(`${include.title} can only be shared to read`)
    if (RANK[include.level] > RANK[level])
      throw new Error(`${include.title} cannot outrank its page`)
  }
  return includes
}

const VIEW_MIMES = new Set([
  'text/html',
  'text/javascript',
  'application/javascript',
])

/** On the inviter's device, before anything is granted (3d). Every include
 * must be signed under the page's own owning group, so a catalog entry an
 * editor planted cannot reach another project or the World. Each must be
 * what it claims: a page, an app, or the view that app names. The label the
 * share dialog showed must be the document's own, so a substituted pointer
 * cannot hide behind a familiar name. Returns the includes with their
 * metadata read off the documents, never off the catalog. */
async function verifyIncludes(
  sharing: Sharing,
  target: InviteTarget,
  level: InviteLevel,
): Promise<InviteInclude[]> {
  const includes = includesOf(target, level)
  if (!includes.length) return []
  const hive = sharing.hive.keyhive
  const page = await sharing.repo.find<{ workspaceId?: unknown }>(
    target.documentId as AnyDocumentId,
  )
  const ownersOf = async (url: Parameters<typeof docIdFromAutomergeUrl>[0]) =>
    (
      (await (await hive.getDocument(docIdFromAutomergeUrl(url)))?.members()) ??
      []
    )
      .filter((c) => c.who.isGroup && c.can.equals(Access.admin()))
      .map((c) => hex(c.who.id.toBytes()))
  const pageOwners = new Set(await ownersOf(page.url))
  if (!pageOwners.size)
    throw new Error('This page has no signed owning group to share under')
  const projectId = String(page.doc()?.workspaceId ?? '')
  const docs = new Map<string, Record<string, unknown>>()
  for (const include of includes) {
    const handle = await sharing.repo.find<Record<string, unknown>>(
      include.documentId as AnyDocumentId,
    )
    const id = docIdFromAutomergeUrl(handle.url)
    if (hex(id.toBytes()) !== include.id)
      throw new Error(`${include.title} does not match its grant`)
    const own = await hive.bestAccessForDoc(hive.id.toIdentifier(), id)
    if (!own?.atLeast(Access.admin()))
      throw new Error(`You cannot share ${include.title}`)
    if (!(await ownersOf(handle.url)).some((g) => pageOwners.has(g)))
      throw new Error(`${include.title} is not in this page's project`)
    const doc = (handle.doc() ?? {}) as Record<string, unknown>
    // A project, World or session root is never something a page draws.
    if (
      'documents' in doc ||
      'apps' in doc ||
      'projects' in doc ||
      'workspaces' in doc ||
      String(doc.workspaceId ?? '') !== projectId
    )
      throw new Error(`${include.title} is not a page of this project`)
    docs.set(include.documentId, doc)
  }
  const apps = includes.filter((i) => i.why === 'app')
  return includes.map((include) => {
    const doc = docs.get(include.documentId) ?? {}
    const path = typeof doc.path === 'string' ? doc.path : ''
    const mime = typeof doc.mime === 'string' ? doc.mime : ''
    if (include.why === 'app') {
      const app = readApp(doc)
      if (!app || app.name !== include.title)
        throw new Error(`${include.title} is not the app it says`)
      return {
        ...include,
        document: undefined,
        app: {
          name: app.name,
          description: String(app.description ?? ''),
          dataType: app.dataType,
          viewDocId: app.viewDocId,
          ...(app.presentation ? { presentation: app.presentation } : {}),
          ...(app.instancesOpenable ? { instancesOpenable: true } : {}),
        },
      }
    }
    if (include.why === 'view') {
      const drawer = apps.find(
        (a) =>
          readApp(docs.get(a.documentId))?.viewDocId === include.documentId,
      )
      if (!drawer || !VIEW_MIMES.has(mime) || path !== include.title)
        throw new Error(`${include.title} is not the view of a shared app`)
      return { ...include, document: undefined, app: undefined }
    }
    if (!path || !mime || path !== include.document?.path)
      throw new Error(`${include.title} is not the page it says`)
    return {
      ...include,
      app: undefined,
      document: {
        path,
        mime,
        ...(typeof doc.dataType === 'string' && doc.dataType
          ? { dataType: doc.dataType }
          : {}),
      },
    }
  })
}

/** Remove one person from one standalone share, without touching grants made
 * through another page. Discover its documents from signed group grants, not
 * from the mutable catalog or the invite's display metadata. */
export async function revokeDocumentShareMember(
  sharing: Sharing,
  target: InviteTarget,
  who: GroupId,
): Promise<string[]> {
  if (target.kind !== 'document' || target.membered.kind !== 'group')
    throw new Error('Not a standalone document share')
  if (!/^[0-9a-f]{64}$/.test(target.membered.id))
    throw new Error('Invalid share group')
  const hive = sharing.hive.keyhive
  const group = new GroupId(unhex(target.membered.id))
  const groupHex = hex(group.toBytes())
  const documents: Array<{ id: string; url: string }> = []
  for (const summary of await hive.reachableDocs()) {
    if (
      (await summary.doc.members()).some(
        (grant) =>
          grant.who.isGroup && hex(grant.who.id.toBytes()) === groupHex,
      )
    )
      documents.push({
        id: hex(summary.doc.docId.toBytes()),
        url: stringifyAutomergeUrl(
          summary.doc.docId.toBytes() as Parameters<
            typeof stringifyAutomergeUrl
          >[0],
        ),
      })
  }
  if (
    !documents.some((document) => document.id === grantIdOf(target.documentId))
  )
    throw new Error('Share group does not grant its page')
  const bound = await guestCommentsGrant(sharing, target.documentId)
  if (!target.commentsId || bound.comments.documentId !== target.commentsId)
    throw new Error('Page comments changed since this share was made')
  // Missing documents must fail before the first revoke. The group removal
  // then adopts retained invitees and sweeps the removed person's keys.
  for (const document of documents)
    await sharing.repo.find(document.url as AnyDocumentId)
  await revokeGroupMemberAndDirectInvitees(hive, group, who)
  const remaining = await hive.bestAccessForDoc(
    who.toIdentifier(),
    new DocumentId(unhex(grantIdOf(target.documentId))),
  )
  if (!remaining?.atLeast(Access.read()))
    await hive.revokeMember(
      who.toIdentifier(),
      true,
      MemberedId.document(bound.id),
    )
  await sharing.persistIdentity()
  for (const document of documents) await admitToHistory(sharing, document.url)
  if (!remaining?.atLeast(Access.read()))
    await admitToHistory(sharing, bound.comments.documentId)
  return documents.map((document) => document.id)
}

/** Remove everyone one standalone page share admitted, by the person ids its
 * settle recorded, then send the revocation and new keys. Another share that
 * names the same include keeps it (3j). The sync is not awaitable: a guest
 * who keeps an include reads its new content after this device's next
 * Keyhive sync, so the caller should stay open briefly rather than quit. */
export async function removeDocumentGuest(
  sharing: Sharing,
  target: InviteTarget,
  personIds: readonly string[],
): Promise<string[]> {
  if (!personIds.length) throw new Error('Nobody joined this share')
  const people = personIds.map((id) => {
    if (!/^[0-9a-f]{64}$/.test(id)) throw new Error('Invalid person id')
    return new GroupId(unhex(id))
  })
  const rekeyed = new Set<string>()
  for (const person of people)
    for (const id of await revokeDocumentShareMember(sharing, target, person))
      rekeyed.add(id)
  sharing.hive.networkAdapter.invalidateCaches()
  sharing.hive.networkAdapter.syncKeyhive()
  return [...rekeyed]
}

const includeId = (include: InviteInclude) =>
  MemberedId.document(new DocumentId(unhex(include.id)))

export interface InviteMaterial {
  seed: Uint8Array
  prekeySecrets: Uint8Array
  /** Signed public events the one-time key needs to act; Keyhive verifies them. */
  events: Uint8Array[]
  level: InviteLevel
  target: InviteTarget
  invitedBy: string
  /** When the invite ends, signed by the device that granted the key. */
  expiry?: InviteExpiry
}

/** How long an invite lasts (3h). */
export const INVITE_MS = 7 * 24 * 60 * 60 * 1000

/** An invite's end, signed by the device that granted its one-time key over
 * the key and what it admits to, so it cannot be moved to another invite or
 * moved later. Acceptance and settlement both check it (RFC 0345 review
 * finding 3). */
export interface InviteExpiry {
  expiresAt: number
  /** The granting device, hex: the signer of the key's own grant. */
  signer: string
  signature: string
}

const expiryBytes = (
  inviteKey: string,
  target: InviteTarget,
  expiresAt: number,
) =>
  canonicalBytes({
    domain: 'mod:sharing:invite-expiry:v1',
    inviteKey,
    membered: { kind: target.membered.kind, id: target.membered.id },
    expiresAt,
  })

/** The device that signed the one-time key's grant on its target, as this
 * hive sees it, or undefined if the key holds none. */
async function inviteGranter(
  hive: Keyhive,
  target: InviteTarget,
  inviteKey: string,
) {
  const grant = (await membersOf(hive, target)).find(
    (c) => hex(c.who.id.toBytes()) === inviteKey,
  )
  return grant ? hex(grant.proof.verifyingKey) : undefined
}

/** Whether the expiry is the one the key's granter signed for this invite. */
async function expiryHolds(
  hive: Keyhive,
  inviteKey: string,
  target: InviteTarget,
  expiry: InviteExpiry | undefined,
) {
  if (!expiry || typeof expiry.expiresAt !== 'number') return false
  const granter = await inviteGranter(hive, target, inviteKey)
  return (
    granter === expiry.signer &&
    (await verifyBytes(
      expiry.signer,
      expiry.signature,
      expiryBytes(inviteKey, target, expiry.expiresAt),
    ))
  )
}

const INFO = new TextEncoder().encode('mod:sharing:invite:v1')
const LEVELS: Record<InviteLevel, () => Access> = {
  admin: Access.admin,
  edit: Access.edit,
  comment: Access.read,
  read: Access.read,
}
const membershipAccess = (target: InviteTarget, level: InviteLevel) =>
  target.kind === 'document' && level === 'comment'
    ? Access.edit()
    : LEVELS[level]()

export const hex = (bytes: Uint8Array) =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
export const unhex = (value: string) =>
  Uint8Array.from(value.match(/../g) ?? [], (pair) => Number.parseInt(pair, 16))
export const inviteDocumentId = (
  url: Parameters<typeof docIdFromAutomergeUrl>[0],
) => hex(docIdFromAutomergeUrl(url).toBytes())
const b64 = (bytes: Uint8Array) => {
  let s = ''
  for (let i = 0; i < bytes.length; i += 0x8000)
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
const unb64 = (value: string) =>
  Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/')), (c) =>
    c.charCodeAt(0),
  )

function membered(target: InviteTarget) {
  if (!/^[0-9a-f]{64}$/.test(target.membered.id))
    throw new Error('Invalid invite target')
  const bytes = unhex(target.membered.id)
  return target.membered.kind === 'group'
    ? MemberedId.group(new GroupId(bytes))
    : MemberedId.document(new DocumentId(bytes))
}

async function sealingKey(secret: Uint8Array) {
  const base = await crypto.subtle.importKey(
    'raw',
    secret as BufferSource,
    'HKDF',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: INFO },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/** The signed events the one-time key needs to prove its own grant. Keyhive's
 * events for an agent leave out the revocations its grant was made after: once
 * anyone has been revoked from the target, the key's grant cites those
 * revocations, and without them the key's Keyhive holds the grant pending and
 * acceptance fails with "No proof found". Each cited revocation is carried in
 * the events of the member it revoked (or, for a person, of their devices). */
async function inviteKeyEvents(
  hive: Keyhive,
  agent: NonNullable<Awaited<ReturnType<Keyhive['getAgent']>>>,
  target: InviteTarget,
  inviteKey: string,
): Promise<Uint8Array[]> {
  const events = new Map<string, Uint8Array>()
  const add = async (who: typeof agent) => {
    for (const bytes of (await hive.eventsForAgent(who)).values())
      events.set(hex(bytes as Uint8Array), bytes as Uint8Array)
  }
  await add(agent)
  const grant = (await membersOf(hive, target)).find(
    (c) => hex(c.who.id.toBytes()) === inviteKey,
  )
  for (const revocation of grant?.proof.delegation.after.revocations() ?? []) {
    const revoked = revocation.delegation.revoked.delegation.delegate
    await add(revoked)
    if (revoked.isGroup)
      for (const member of (await (
        await hive.getGroup(new GroupId(revoked.id.toBytes()))
      )?.transitiveMembers()) ?? [])
        await add(member.who)
  }
  return [...events.values()]
}

/** Mint the one-time key and grant it the level. Returns the link secret,
 * which must never be sent to the server, and the sealed material, which is. */
export async function createInvite(
  sharing: Sharing,
  options: {
    level: InviteLevel
    target: InviteTarget
    invitedBy: string
    /** The inviter's person owns a standalone share across their devices. */
    ownerPerson?: GroupId
    /** When it ends; at most seven days, the default. */
    expiresAt?: number
  },
): Promise<{
  inviteKey: string
  secret: string
  sealed: string
  /** Signed into the sealed material; the ledger keeps it to settle by. */
  expiry: InviteExpiry
  /** The target as sealed, its includes' metadata read off the documents. */
  target: InviteTarget
}> {
  if (
    (options.target.kind === 'document') !==
    (options.target.membered.kind === 'document')
  )
    throw new Error('Invite scope does not match its target')
  if (options.level === 'comment' && options.target.kind !== 'document')
    throw new Error('Can comment is for a document')
  const expiresAt = options.expiresAt ?? Date.now() + INVITE_MS
  if (expiresAt <= Date.now() || expiresAt > Date.now() + INVITE_MS + 60_000)
    throw new Error('An invite lasts at most seven days')
  const hive = sharing.hive.keyhive
  const root = await sharing.repo.find(
    options.target.documentId as AnyDocumentId,
  )
  const rootId = docIdFromAutomergeUrl(root.url)
  if (options.target.membered.kind === 'document') {
    if (hex(rootId.toBytes()) !== options.target.membered.id)
      throw new Error('Invite document does not match its grant')
  } else {
    const protectedRoot = await hive.getDocument(rootId)
    const owners = await protectedRoot?.members()
    if (
      !owners?.some(
        (c) =>
          c.who.isGroup &&
          c.can.equals(Access.admin()) &&
          hex(c.who.id.toBytes()) === options.target.membered.id,
      )
    )
      throw new Error('Invite group does not own this document')
  }
  if (
    options.target.kind === 'document' &&
    grantIdOf(options.target.documentId) !== options.target.membered.id
  )
    throw new Error('Invite document does not match its grant')
  // Each include is checked before anything is granted: one this person
  // cannot hand on, or that is not what the dialog named, refuses the invite.
  const includes = await verifyIncludes(sharing, options.target, options.level)
  let target: InviteTarget = includes.length
    ? { ...options.target, includes }
    : options.target
  // Validate the target before provisioning an older page's missing comments.
  if (options.target.kind === 'document') {
    if (!options.ownerPerson)
      throw new Error('A document share needs its owner person')
    const person = await hive.getGroup(options.ownerPerson)
    const current = hex(hive.id.toBytes())
    if (
      !(await person?.transitiveMembers())?.some(
        (member) =>
          hex(member.who.id.toBytes()) === current &&
          member.can.equals(Access.admin()),
      )
    )
      throw new Error('This device is not in the share owner’s person')
    const comments = await provisionGuestComments(
      sharing,
      options.target.documentId,
    )
    // Each invite is one independent share. Revoking membership in its group
    // cannot remove a grant made through another page's share group.
    const share = await hive.generateGroup([options.ownerPerson.toIdentifier()])
    await hive.revokeMember(
      hive.id.toIdentifier(),
      true,
      MemberedId.group(share),
    )
    await hive.addMember(
      share.toIdentifier(),
      MemberedId.document(rootId),
      LEVELS[options.level](),
      [],
    )
    for (const include of includes)
      await hive.addMember(
        share.toIdentifier(),
        includeId(include),
        LEVELS[include.level](),
        [],
      )
    // Comments retain their existing person grants. A second group grant on
    // the comments document strands an existing reader's writes at the relay.
    target = {
      ...target,
      membered: { kind: 'group', id: hex(share.toBytes()) },
      commentsId: comments.comments.documentId,
    }
    await sharing.persistIdentity()
  }
  const seed = crypto.getRandomValues(new Uint8Array(32))
  const secret = crypto.getRandomValues(new Uint8Array(32))
  let key: Keyhive | undefined
  let prekeySecrets: Uint8Array | undefined
  try {
    key = await Keyhive.init(
      Signer.memorySignerFromBytes(seed),
      CiphertextStore.newInMemory(),
      () => {},
    )
    const card = ContactCard.fromJson((await key.contactCard()).toJson())
    prekeySecrets = await key.exportPrekeySecrets()
    await hive.receiveContactCard(card)
    // A contact card alone stays on this device, so every peer (the relay and
    // this person's other tabs too) keeps the grant below pending until the
    // invite is redeemed, and forever if it never is. Carry the key's own
    // signed prekey events in this device's history so they sync with it.
    await hive.ingestEventsBytes([
      ...(await key.allAgentEvents()).events.values(),
    ])
    await hive.addMember(
      card.id,
      membered(target),
      membershipAccess(target, options.level),
      [],
    )
    if (target.kind !== 'document')
      for (const include of includes)
        await hive.addMember(
          card.id,
          includeId(include),
          LEVELS[include.level](),
          [],
        )
    await sharing.persistIdentity()
    const agent = await hive.getAgent(card.id)
    if (!agent) throw new Error('Invite key was not recorded')
    const events = await inviteKeyEvents(
      hive,
      agent,
      target,
      hex(card.id.toBytes()),
    )
    const inviteKey = hex(card.id.toBytes())
    const expiry: InviteExpiry = {
      expiresAt,
      signer: hex(hive.id.toBytes()),
      signature: await signBytes(
        sharing,
        expiryBytes(inviteKey, target, expiresAt),
      ),
    }
    const plain = new TextEncoder().encode(
      JSON.stringify({
        v: 1,
        seed: b64(seed),
        prekeySecrets: b64(prekeySecrets),
        events: events.map((e: Uint8Array) => b64(e)),
        level: options.level,
        target,
        invitedBy: options.invitedBy,
        expiry,
      }),
    )
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const cipher = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        await sealingKey(secret),
        plain,
      ),
    )
    plain.fill(0)
    const sealed = new Uint8Array(12 + cipher.length)
    sealed.set(iv)
    sealed.set(cipher, 12)
    return {
      inviteKey,
      target,
      secret: b64(secret),
      sealed: b64(sealed),
      expiry,
    }
  } finally {
    seed.fill(0)
    secret.fill(0)
    prekeySecrets?.fill(0)
    key?.free()
  }
}

export async function openInvite(
  secret: string,
  sealed: string,
): Promise<InviteMaterial> {
  const bytes = unb64(sealed)
  const key = unb64(secret)
  if (key.length !== 32 || bytes.length < 29)
    throw new Error('This invite link is incomplete')
  let plain: Uint8Array
  try {
    plain = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: bytes.subarray(0, 12) },
        await sealingKey(key),
        bytes.subarray(12),
      ),
    )
  } catch {
    throw new Error('This invite link does not match its invite')
  } finally {
    key.fill(0)
  }
  const value = JSON.parse(new TextDecoder().decode(plain))
  plain.fill(0)
  if (value?.v !== 1 || !(value.level in LEVELS))
    throw new Error('Unknown invite format')
  return {
    seed: unb64(value.seed),
    prekeySecrets: unb64(value.prekeySecrets),
    events: (value.events as string[]).map(unb64),
    level: value.level,
    target: value.target,
    invitedBy: String(value.invitedBy ?? ''),
    ...(value.expiry ? { expiry: value.expiry as InviteExpiry } : {}),
  }
}

/** The guest's device acts once as the invite key to admit its own person.
 * Refuses once the invite's signed expiry has passed. Consumes and wipes the
 * material whether or not it succeeds. */
export async function acceptInvite(
  sharing: Sharing,
  material: InviteMaterial,
  person: GroupId,
  now = Date.now(),
): Promise<void> {
  const hive = sharing.hive.keyhive
  let key: Keyhive | undefined
  try {
    if (material.target.kind === 'document') {
      if (material.target.membered.kind !== 'group')
        throw new Error('Document invite has no share group')
    }
    key = await Keyhive.init(
      Signer.memorySignerFromBytes(material.seed),
      CiphertextStore.newInMemory(),
      () => {},
    )
    await key.importPrekeySecrets(material.prekeySecrets)
    await key.ingestEventsBytes(material.events)
    // The end is signed by the device that granted this key, over the key and
    // its target: material fetched in time is still refused once it passes.
    const inviteKey = hex(key.id.toBytes())
    if (!(await inviteGranter(key, material.target, inviteKey)))
      throw new Error(
        'This invite key cannot prove its grant: its link is missing history. Ask for a new one.',
      )
    if (!(await expiryHolds(key, inviteKey, material.target, material.expiry)))
      throw new Error('This invite has no valid expiry')
    if (now >= material.expiry!.expiresAt)
      throw new Error('This invite has expired. Ask for a new one.')
    if (material.target.kind === 'document') {
      const page = new DocumentId(unhex(grantIdOf(material.target.documentId)))
      const access = await key.bestAccessForDoc(key.id.toIdentifier(), page)
      if (!access?.atLeast(LEVELS[material.level]()))
        throw new Error('Document share group does not grant its page')
      for (const include of includesOf(material.target, material.level)) {
        const reach = await key.bestAccessForDoc(
          key.id.toIdentifier(),
          new DocumentId(unhex(include.id)),
        )
        if (!reach?.atLeast(LEVELS[include.level]()))
          throw new Error(`${include.title} is not in this share`)
      }
    }
    if (!(await hive.hasGroup(person)))
      throw new Error('This device has no person to admit')
    // The guest's own signed events define its person and devices.
    await key.ingestEventsBytes([
      ...(await hive.allAgentEvents()).events.values(),
    ])
    await key.addMember(
      person.toIdentifier(),
      membered(material.target),
      membershipAccess(material.target, material.level),
      [],
    )
    if (material.target.kind !== 'document')
      for (const include of includesOf(material.target, material.level))
        await key.addMember(
          person.toIdentifier(),
          includeId(include),
          LEVELS[include.level](),
          [],
        )
    await hive.ingestEventsBytes([
      ...(await key.allAgentEvents()).events.values(),
    ])
    await sharing.persistIdentity()
  } finally {
    material.seed.fill(0)
    material.prekeySecrets.fill(0)
    key?.free()
  }
}

/** On the owner's device: anyone the invite key admitted is granted again
 * from this device's person, then the key is revoked, so nobody's access
 * hangs on a key that is about to go (3j). The invite's signed expiry is
 * checked first: past it, or without a valid one, nobody is admitted and
 * anyone the key let in is revoked with it. Returns who was admitted. */
export async function settleInvite(
  sharing: Sharing,
  options: {
    inviteKey: string
    target: InviteTarget
    level: InviteLevel
    expired: boolean
    /** The signed expiry the invite was made with (createInvite). */
    expiry?: InviteExpiry
    /** Who may join: the invited address's own person (hex), or a check of
     * a joiner against the account location that address's account records.
     * A check throws when it cannot tell, and the invite stays pending.
     * Forwarding is not allowed (RFC 0345 review finding 4). */
    invitee: string | ((person: string) => Promise<boolean>)
    /** RFC 0345 3h (S16): the invited address already has an account, and
     * this is the person it recorded. Admitted now, as if they had joined
     * through the key, which is then revoked. */
    direct?: string
    now?: number
  },
): Promise<{ admitted: string[]; settled: boolean; refused?: string[] }> {
  const hive = sharing.hive.keyhive
  const keyId = new Identifier(unhex(options.inviteKey))
  const target = membered(options.target)
  const includes = includesOf(options.target, options.level)
  const revoke = async (who: Identifier) => {
    if (options.target.kind !== 'document')
      for (const include of includes)
        await hive.revokeMember(who, true, includeId(include))
    await hive.revokeMember(who, true, target)
  }
  const members = await membersOf(hive, options.target)
  const keyHolds = members.some((c) =>
    sameBytes(c.who.id.toBytes(), keyId.toBytes()),
  )
  if (!keyHolds) return { admitted: [], settled: true }
  const joiners = members
    .filter(
      (c) =>
        sameBytes(c.proof.verifyingKey, keyId.toBytes()) &&
        hex(c.who.id.toBytes()) !== options.inviteKey,
    )
    .map((c) => c.who.id as Identifier)
  if (
    options.direct &&
    /^[0-9a-f]{64}$/.test(options.direct) &&
    !joiners.some((who) => hex(who.toBytes()) === options.direct)
  )
    joiners.push(new Identifier(unhex(options.direct)))
  const now = options.now ?? Date.now()
  const valid = await expiryHolds(
    hive,
    options.inviteKey,
    options.target,
    options.expiry,
  )
  const ended = options.expired || !valid || now >= options.expiry!.expiresAt
  if (ended && joiners.length) {
    // A join this device did not settle before the end is late: the key is
    // not trusted to say when it signed, so the end decides (review 3).
    for (const who of joiners) await revoke(who)
    await revoke(keyId)
    await sharing.persistIdentity()
    return {
      admitted: [],
      settled: true,
      refused: [
        valid
          ? 'This invite expired before the join was settled'
          : 'This invite has no valid expiry',
      ],
    }
  }
  // Only the invited address's person joins. Anyone else the key let in (a
  // forwarded payload) is revoked by name, and so is the key.
  const invited: Identifier[] = []
  const forwarded: Identifier[] = []
  try {
    for (const who of joiners) {
      const id = hex(who.toBytes())
      const yes =
        typeof options.invitee === 'string'
          ? options.invitee === id
          : await options.invitee(id)
      ;(yes ? invited : forwarded).push(who)
    }
  } catch {
    return { admitted: [], settled: false }
  }
  if (forwarded.length) {
    for (const who of forwarded) await revoke(who)
    await sharing.persistIdentity()
    joiners.splice(0, joiners.length, ...invited)
    if (!joiners.length) {
      await revoke(keyId)
      await sharing.persistIdentity()
      return {
        admitted: [],
        settled: true,
        refused: ['Joined from an account the invite was not sent to'],
      }
    }
  }
  if (joiners.length && options.target.kind === 'document') {
    // A guest's comments must follow the page. If they cannot, grant neither:
    // the key goes and the guest falls back to nothing, with the reason kept.
    try {
      if (options.target.membered.kind !== 'group')
        throw new Error('Document invite has no share group')
      const share = options.target.membered.id
      if (
        !(await shareGrantsDocument(
          hive,
          share,
          new DocumentId(unhex(grantIdOf(options.target.documentId))),
          LEVELS[options.level](),
        ))
      )
        throw new Error('Share group does not grant its page')
      for (const include of includes) {
        if (
          !(await shareGrantsDocument(
            hive,
            share,
            new DocumentId(unhex(include.id)),
            LEVELS[include.level](),
          ))
        )
          throw new Error(`${include.title} is not in this share`)
      }
      const bound = await guestCommentsGrant(sharing, options.target.documentId)
      if (
        !options.target.commentsId ||
        bound.comments.documentId !== options.target.commentsId
      )
        throw new Error('Share does not grant its comments binding')
    } catch (error) {
      // Revoking the key retains what it delegated, so the guests it let in
      // are revoked by name first.
      for (const who of joiners) await revoke(who)
      await revoke(keyId)
      await sharing.persistIdentity()
      return {
        admitted: [],
        settled: true,
        refused: [(error as Error).message],
      }
    }
  }
  const admitted: string[] = []
  const comments = new Set<string>()
  const refused: string[] = []
  for (const who of joiners) {
    await hive.addMember(
      who,
      target,
      membershipAccess(options.target, options.level),
      [],
    )
    const opened = await grantComments(sharing, options, who)
    for (const id of opened.granted) comments.add(id)
    refused.push(...opened.refused)
    admitted.push(hex(who.toBytes()))
  }
  if (forwarded.length)
    refused.push('Joined from an account the invite was not sent to')
  if (admitted.length || ended) {
    await revoke(keyId)
    await sharing.persistIdentity()
    if (admitted.length) {
      await admitToHistory(sharing, options.target.documentId)
      for (const include of includes)
        await admitToHistory(sharing, include.documentId)
      if (options.target.kind !== 'document')
        for (const id of await boundCommentsUnder(sharing, options.target))
          comments.add(id)
      // A new member needs the secrets of comments written before they came.
      for (const id of comments) await admitToHistory(sharing, id)
      if (options.target.kind === 'project') {
        const project = await sharing.repo.find<{
          documents?: Record<string, unknown>
          apps?: Record<string, unknown>
        }>(options.target.documentId as AnyDocumentId)
        for (const id of new Set([
          ...Object.keys(project.doc().documents ?? {}),
          ...Object.keys(project.doc().apps ?? {}),
        ])) {
          const child = await sharing.repo.find(id as AnyDocumentId)
          const protectedChild = await hive.getDocument(
            docIdFromAutomergeUrl(child.url),
          )
          const grants = await protectedChild?.members()
          // Catalog pointers cannot widen a grant to unrelated private content.
          if (
            grants?.some(
              (c) =>
                c.who.isGroup &&
                hex(c.who.id.toBytes()) === options.target.membered.id,
            )
          )
            await admitToHistory(sharing, id)
        }
      }
    }
    return { admitted, settled: true, ...(refused.length ? { refused } : {}) }
  }
  return { admitted, settled: false }
}

async function membersOf(hive: Keyhive, target: InviteTarget) {
  const bytes = unhex(target.membered.id)
  if (target.membered.kind === 'group') {
    const group = await hive.getGroup(new GroupId(bytes))
    return (await group?.members()) ?? []
  }
  const doc = await hive.getDocument(new DocumentId(bytes))
  return (await doc?.members()) ?? []
}

async function shareGrantsDocument(
  hive: Keyhive,
  share: string,
  document: DocumentId,
  required: Access,
) {
  const members = await (await hive.getDocument(document))?.members()
  return Boolean(
    members?.some(
      (grant) =>
        grant.who.isGroup &&
        hex(grant.who.id.toBytes()) === share &&
        grant.can.atLeast(required),
    ),
  )
}

function sameBytes(a: Uint8Array, b: Uint8Array) {
  return a.length === b.length && a.every((byte, i) => byte === b[i])
}

/** A grant alone does not give a new reader the secrets of history written
 * before it. Rotate the document's key and write once, so the predecessor
 * chain carries them (the same step a new device takes, personal-world.ts). */
export async function admitToHistory(sharing: Sharing, documentId: string) {
  const handle = await sharing.repo.find<Record<string, unknown>>(
    documentId as AnyDocumentId,
  )
  // The admitting device rotates, even in a hosted session. Deferring this to
  // the host (rotator.ts) left a guest waiting to be let in forever in the
  // live test of Oct 27, though the host did rotate; rotating here is what
  // let guests in on Oct 25. The host still rotates last (Keyhive patch).
  const secret = await sharing.hive.keyhive.forcePcsUpdate(
    docIdFromAutomergeUrl(handle.url),
  )
  try {
    await sharing.hive.keyhiveStorage.saveLeafSecret(secret)
  } finally {
    secret.fill(0)
  }
  handle.change((doc) => {
    doc.sharingAdmittedAt = Date.now()
  })
  await sharing.repo.flush()
  await sharing.persistIdentity()
}
